import React, { useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { safeWrite } from '@/utils/supabaseWrite';
import { vibrateTap } from '@/utils/sound';

// ── Flag illustrations ──────────────────────────────────────────────────
// Inline SVG renders identically everywhere (regional-indicator emoji
// fall back to uppercase letters on some Linux / Windows builds).

const styles = {
  screen: {
    position: 'fixed',
    inset: 0,
    zIndex: 10000,
    background: 'var(--bg)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding:
      'max(28px, env(safe-area-inset-top)) 24px max(28px, env(safe-area-inset-bottom))',
    overflow: 'hidden',
  },
  halo: {
    position: 'absolute',
    top: '32%',
    left: '50%',
    width: 420,
    height: 420,
    marginLeft: -210,
    marginTop: -240,
    borderRadius: '50%',
    background:
      'radial-gradient(closest-side, var(--primary-pale), transparent 70%)',
    filter: 'blur(4px)',
    pointerEvents: 'none',
    animation: 'motionHaloBreathe 4000ms cubic-bezier(0.45,0,0.55,1) infinite',
  },
  stage: {
    position: 'relative',
    width: '100%',
    maxWidth: 440,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 14,
  },
  preludeWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    marginBottom: 2,
  },
  logo: {
    width: 96,
    height: 'auto',
    display: 'block',
  },
  title: {
    margin: '8px 0 0',
    textAlign: 'center',
    fontFamily: 'var(--font-title)',
    fontStyle: 'italic',
    fontWeight: 400,
    fontSize: 44,
    lineHeight: 1.05,
    letterSpacing: '-0.02em',
    color: 'var(--dark-brown)',
    fontVariationSettings: '"opsz" 96',
  },
  sub: {
    margin: '4px 0 18px',
    textAlign: 'center',
    fontFamily: 'var(--font-body)',
    fontSize: 18,
    lineHeight: 1.6,
    color: 'var(--warm-brown)',
  },
  cards: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    marginTop: 4,
  },
  hint: {
    marginTop: 14,
    textAlign: 'center',
    fontFamily: 'var(--font-ui)',
    fontSize: 12,
    letterSpacing: '0.04em',
    color: 'var(--warm-brown)',
    opacity: 0.8,
  },
};

function FlagAU() {
  return (
    <svg
      width="42"
      height="28"
      viewBox="0 0 36 24"
      aria-hidden="true"
      style={{
        borderRadius: 4,
        overflow: 'hidden',
        boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
        flexShrink: 0,
      }}
    >
      <rect width="36" height="24" fill="#012169" />
      <rect width="18" height="12" fill="#012169" />
      <path d="M0 0 L18 12 M18 0 L0 12" stroke="#FFFFFF" strokeWidth="2.2" />
      <path d="M0 0 L18 12 M18 0 L0 12" stroke="#C8102E" strokeWidth="1.1" />
      <path d="M9 0 V12 M0 6 H18" stroke="#FFFFFF" strokeWidth="3" />
      <path d="M9 0 V12 M0 6 H18" stroke="#C8102E" strokeWidth="1.5" />
      <circle cx="9" cy="18" r="2" fill="#FFFFFF" />
      <circle cx="26" cy="6" r="1.1" fill="#FFFFFF" />
      <circle cx="32" cy="11" r="1" fill="#FFFFFF" />
      <circle cx="27" cy="17" r="1" fill="#FFFFFF" />
      <circle cx="32" cy="20" r="0.8" fill="#FFFFFF" />
      <circle cx="22" cy="15" r="0.7" fill="#FFFFFF" />
    </svg>
  );
}

function FlagBR() {
  return (
    <svg
      width="42"
      height="28"
      viewBox="0 0 36 24"
      aria-hidden="true"
      style={{
        borderRadius: 4,
        overflow: 'hidden',
        boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
        flexShrink: 0,
      }}
    >
      <rect width="36" height="24" fill="#009C3B" />
      <polygon points="18,3 33,12 18,21 3,12" fill="#FFDF00" />
      <circle cx="18" cy="12" r="5.6" fill="#002776" />
      <path d="M13 13.2 Q 18 11 23 13.2" stroke="#FFFFFF" strokeWidth="1.1" fill="none" />
    </svg>
  );
}

// ── Card primitive ───────────────────────────────────────────────────

function LanguageCard({
  onClick,
  disabled,
  selected,
  flag,
  title,
  subtitle,
  stagger,
  ariaLabel,
}) {
  const [pressed, setPressed] = useState(false);

  const style = {
    position: 'relative',
    width: '100%',
    minHeight: 96,
    display: 'flex',
    alignItems: 'center',
    gap: 18,
    textAlign: 'left',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderLeft: selected
      ? '4px solid var(--primary)'
      : '4px solid transparent',
    borderRadius: 18,
    padding: '20px 22px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    color: 'var(--text)',
    WebkitTapHighlightColor: 'transparent',
    transition:
      'border-color 180ms ease, transform 140ms cubic-bezier(0.2,0.8,0.2,1), opacity 180ms ease, box-shadow 240ms ease',
    transform: selected ? 'scale(0.985)' : pressed ? 'scale(0.98)' : 'scale(1)',
    opacity: disabled ? 0.55 : selected ? 0.92 : 1,
    boxShadow: selected
      ? '0 14px 28px -14px rgba(168, 0, 63, 0.28)'
      : '0 2px 14px rgba(45, 26, 31, 0.05)',
    '--stagger-i': stagger,
  };

  return (
    <button
      type="button"
      className="motion-rise"
      style={style}
      onClick={onClick}
      disabled={disabled}
      onPointerDown={() => !disabled && setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerCancel={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      aria-pressed={selected || undefined}
      aria-label={ariaLabel}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>{flag}</span>
      <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span
          style={{
            fontFamily: 'var(--font-title)',
            fontStyle: 'italic',
            fontSize: 26,
            fontWeight: 500,
            color: 'var(--text)',
            lineHeight: 1.1,
            letterSpacing: '-0.012em',
            fontVariationSettings: '"opsz" 64',
          }}
        >
          {title}
        </span>
        <span
          style={{
            fontFamily: 'var(--font-ui)',
            fontSize: 13,
            fontWeight: 500,
            letterSpacing: '0.02em',
            color: 'var(--warm-brown)',
          }}
        >
          {subtitle}
        </span>
      </span>
      {/* Chevron — subtle, nudges on press. Keeps the card reading as a
          forward-moving action rather than a static tile. */}
      <span
        aria-hidden="true"
        style={{
          width: 28,
          height: 28,
          borderRadius: 14,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--primary-pale)',
          color: 'var(--primary)',
          fontSize: 16,
          fontWeight: 500,
          transform: selected ? 'translateX(2px)' : 'translateX(0)',
          transition: 'transform 160ms ease',
          flexShrink: 0,
        }}
      >
        {'→'}
      </span>
    </button>
  );
}

// ── Main component ─────────────────────────────────────────────────────

// First screen a new user sees after SSO login, before ConsentMessage.
// The greeting + instruction are rendered in a single language picked
// from `navigator.language` (pt-* → Portuguese, anything else →
// English) so the prelude reads as one voice instead of stacked
// translations. Both LanguageCard buttons stay visible so the user
// can still pick the other language — each card is self-labelled in
// its own language ("English / Continue in English",
// "Português / Continuar em Português"), so a Portuguese reader
// landing on an English prelude still understands which card to tap.
//
// On selection we optimistically advance (see App.jsx AppShell) and
// mirror to Supabase in the background. safeWrite persists bb7 to
// localStorage synchronously before its first await, so the local
// source of truth is on disk before this function returns.

// Detect the browser's preferred language for the prelude copy.
// Anything starting with `pt` (pt-BR, pt-PT, pt) → Portuguese,
// everything else → English. SSR-safe: defaults to English when
// `navigator` is undefined.
function detectInitialLang() {
  if (typeof navigator === 'undefined') return 'en';
  const lang = String(navigator.language || '').toLowerCase();
  return lang.startsWith('pt') ? 'pt' : 'en';
}

const PRELUDE_COPY = {
  en: {
    title: 'Welcome.',
    sub: 'Choose the language you want to feel this in.',
    hint: 'You can change this anytime in your Profile.',
  },
  pt: {
    title: 'Boas-vindas.',
    sub: 'Escolha o idioma em que quer sentir isso.',
    hint: 'Pode alterar a qualquer momento no seu Perfil.',
  },
};

export default function LanguageSelector({ onComplete }) {
  const { user, setLang } = useApp();

  const [selected, setSelected] = useState(null); // 'en' | 'pt' | null
  const [saving, setSaving] = useState(false);
  // Detection is one-shot — once we render in the user's browser
  // language we don't want to flip it under them mid-session.
  const [preludeLang] = useState(detectInitialLang);
  const copy = PRELUDE_COPY[preludeLang];

  async function handleSelect(choice) {
    if (saving) return;
    try { vibrateTap(); } catch { /* iOS Safari pre-13.4 */ }
    setSelected(choice);
    setSaving(true);

    // Brief acknowledgement window so the tap feels connected to the
    // screen swap. Matches the 180ms transform transition on the card.
    await new Promise((r) => setTimeout(r, 220));

    try {
      let data = {};
      try {
        const saved = localStorage.getItem('bb7');
        if (saved) data = JSON.parse(saved);
      } catch {}
      data.lang = choice;
      data.language_selected = true;
      // Fire-and-forget. safeWrite writes bb7 to localStorage
      // synchronously (before its first await), so the local source of
      // truth is persisted before this call returns a pending promise.
      safeWrite(user?.id, data).catch((err) => {
        console.error('LanguageSelector background save failed:', err);
      });
      setLang(choice);
      // Optimistic gate flip — see App.jsx AppShell for the full
      // rationale on why we don't await a Supabase refetch here.
      onComplete();
    } catch (err) {
      console.error('LanguageSelector save failed:', err);
      setSaving(false);
      setSelected(null);
    }
  }

  return (
    <div style={styles.screen}>
      {/* Ambient brand halo — the same motif as LoadingScreen /
          ErrorBoundary so the first three screens rhyme visually. */}
      <span aria-hidden="true" style={styles.halo} />

      <div style={styles.stage}>
        {/* Prelude: brand logo. */}
        <div
          className="motion-rise"
          style={{ ...styles.preludeWrap, '--stagger-i': 0 }}
        >
          <img
            src="/logo.png"
            alt="Be Bold"
            width="96"
            height="96"
            style={styles.logo}
          />
        </div>

        {/* Single-language headline matched to the browser's preferred
            locale so the prelude reads as one voice. Both language
            cards below stay visible — each self-labelled in its own
            tongue — so a user whose browser doesn't match their
            preferred language can still pick the other one. */}
        <h1
          className="motion-rise"
          style={{ ...styles.title, '--stagger-i': 1 }}
          lang={preludeLang}
        >
          {copy.title}
        </h1>

        <p
          className="motion-rise"
          style={{ ...styles.sub, '--stagger-i': 2 }}
          lang={preludeLang}
        >
          {copy.sub}
        </p>

        <div style={styles.cards}>
          <LanguageCard
            onClick={() => handleSelect('en')}
            disabled={saving && selected !== 'en'}
            selected={selected === 'en'}
            flag={<FlagAU />}
            title="English"
            subtitle="Continue in English"
            stagger={3}
            ariaLabel="Continue in English"
          />
          <LanguageCard
            onClick={() => handleSelect('pt')}
            disabled={saving && selected !== 'pt'}
            selected={selected === 'pt'}
            flag={<FlagBR />}
            title="Português"
            subtitle="Continuar em Português"
            stagger={4}
            ariaLabel="Continuar em Português"
          />
        </div>

        <p
          className="motion-rise"
          style={{ ...styles.hint, '--stagger-i': 5 }}
          lang={preludeLang}
        >
          {copy.hint}
        </p>
      </div>
    </div>
  );
}

